/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */

import { useTranslations } from '@/hooks/useTranslations';
import { createStyles } from '@/utils/styles';
import {
    Bar,
    Button,
    Card,
    FlexBox,
    FlexBoxAlignItems,
    FlexBoxDirection,
    FlexBoxJustifyContent,
    FlexBoxWrap,
    IllustratedMessage,
    MessageStrip,
    ProgressIndicator,
    Title,
    Text,
} from '@ui5/webcomponents-react';
import React from 'react';
import { Link } from 'react-router-dom';
import { useGetProductRunByUserIdAndFlowIdQuery, useGetSubscriptionsQuery, useGetLaunchSummaryQuery, useGetUserInfoQuery } from '@/store/apis';
import { getLaunchedProducts, getProductByFlowId } from '@/utils/utils';
import StepProgress from './StepProgress/StepProgress';
import NavigableSidebar from '@/components/NavigableSidebar/NavigableSidebar';
import { ThemingParameters } from '@ui5/webcomponents-react-base';
import { uiFullWidth } from '@/Styling/Theme/Theme';

interface Props {
    flowId: string;
    skuId: number;
}

const PersonalizationMonitorPage: React.FC<Props> = ({ flowId, skuId }) => {
    const { t } = useTranslations();
    const { data: userInfo } = useGetUserInfoQuery();
    const { data: productRun } = useGetProductRunByUserIdAndFlowIdQuery(
        {
            userId: userInfo?.scim_id ?? '',
            flowId,
        },
        { skip: skuId === 1 || skuId === 2 || !userInfo || !flowId, refetchOnMountOrArgChange: true }
    );

    const { data: launchSummary, refetch } = useGetLaunchSummaryQuery(productRun?.id ?? '', {
        skip: !productRun?.id,
        refetchOnMountOrArgChange: true,
    });

    const { data: productsSubscriptions } = useGetSubscriptionsQuery();
    const launchedProducts = getLaunchedProducts(productsSubscriptions);
    const product = getProductByFlowId(flowId, productsSubscriptions);

    const getPercentageCompleted = () => {
        if (!launchSummary) return;
        const { steps } = launchSummary;
        const failedSteps = steps.filter(step => step.status === 'FAILED');
        if (failedSteps.length > 0 || steps.length === 0) {
            return 100;
        }
        const completedSteps = steps.filter(step => step.status === 'COMPLETED');
        return Math.floor((completedSteps.length / steps.length) * 100);
    };

    const getValueStateProgress = () => {
        if (!launchSummary) return;
        const { steps } = launchSummary;
        if (steps.length === 0) {
            return 'Positive';
        }
        const percentageCompleted = getPercentageCompleted();
        if (percentageCompleted === 0) {
            return 'None';
        }
        const failedSteps = steps.filter(step => step.status === 'FAILED');
        if (failedSteps.length > 0) {
            return 'Negative';
        }
        if (percentageCompleted === 100) {
            return 'Positive';
        }
        return 'Information';
    };

    return (
        <div style={styles.wrapper}>
            <Bar
                design="Subheader"
                startContent={<Title size="H3">{t('PERSONALIZATION_MONITOR_PAGE_TITLE')} </Title>}
                {...(productRun?.realm && { endContent: <Text data-testId="realm-text">{t('REALM', productRun.realm)}</Text> })}
            />

            <FlexBox gap={16} style={styles.contentWrapper} wrap={FlexBoxWrap.Wrap} alignItems={FlexBoxAlignItems.Start}>
                <NavigableSidebar headerText={t('MONITOR_PROGRESS')} currentId={flowId} items={launchedProducts} />
                <Card data-sap-ui-fastnavgroup="true" style={{ maxWidth: 864 }}>
                    <FlexBox style={{ padding: '20px', marginBottom: ThemingParameters.sapContent_Padding_M }}>
                        <FlexBox
                            direction={FlexBoxDirection.Row}
                            justifyContent={FlexBoxJustifyContent.SpaceBetween}
                            alignItems={FlexBoxAlignItems.End}
                            style={{ flex: 1 }}
                        >
                            <Title style={{ flex: 2 }}>{product?.productDto?.name}</Title>
                            <ProgressIndicator
                                style={{ flex: 1, paddingTop: '20px', paddingRight: '20px', paddingBottom: '6px' }}
                                value={getPercentageCompleted()}
                                valueState={getValueStateProgress()}
                            />
                        </FlexBox>
                    </FlexBox>
                    {launchSummary?.steps && launchSummary?.steps?.length > 0 ? (
                        launchSummary?.steps?.map((step, index: number) => (
                            <StepProgress key={`${step.name}-${index}`} name={step.name} status={step.status} activity={step.activity} />
                        ))
                    ) : (
                        <IllustratedMessage name="SimpleEmptyList" />
                    )}
                    <FlexBox style={styles.itemAlignWrapper}>
                        <MessageStrip design="Information" hideCloseButton>
                            {t('MONITOR_HELP_TEXT')}
                        </MessageStrip>
                    </FlexBox>

                    <FlexBox style={styles.itemAlignWrapper} justifyContent={FlexBoxJustifyContent.End}>
                        <Button
                            type="Button"
                            design="Default"
                            disabled={getPercentageCompleted() !== 100}
                            onClick={() => {
                                const newWindow = window.open(launchSummary?.tenantUrl, '_blank', 'noopener noreferrer');
                                if (newWindow) {
                                    newWindow.opener = null;
                                }
                            }}
                        >
                            {t('OPEN_APPLICATION_BUTTON')}
                        </Button>
                    </FlexBox>
                </Card>
            </FlexBox>

            <div style={styles.footerWrapper}>
                <Bar
                    design="FloatingFooter"
                    endContent={
                        <FlexBox style={{ gap: 8 }}>
                            <Button type="Button" design="Default" icon="refresh" onClick={refetch}>
                                {t('REFRESH_BUTTON')}
                            </Button>
                            <Link to="/">
                                <Button type="Button" design="Emphasized">
                                    {t('HOME_BUTTON')}
                                </Button>
                            </Link>
                        </FlexBox>
                    }
                />
            </div>
        </div>
    );
};

const styles = createStyles({
    wrapper: {
        height: '100vh',
        overflowY: 'auto',
    },

    contentWrapper: {
        padding: '16px 48px',
        gap: 16,
    },
    footerWrapper: {
        padding: 8,
        left: 0,
        bottom: 0,
        ...uiFullWidth,
        position: 'fixed',
    },
    itemAlignWrapper: {
        marginLeft: ThemingParameters.sapContent_Margin_Small,
        marginRight: ThemingParameters.sapContent_Margin_Small,
        marginBottom: ThemingParameters.sapContent_Margin_Large,
    },
});

export default PersonalizationMonitorPage;
